/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useMaterialUIController } from "../../context";
import {
  GetDashboardReportCall,
  GetMapReportCall,
} from "../../redux/api/report.api";
import SimpleMap from "./components/SimpleMap";
import { isEmpty } from "lodash";
import { StatsLoadingSkeleton } from "components/InstagramLoader";

function Dashboard({ noLayout = false, hideMap = false }) {
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const report = useSelector((state) => state?.report?.report);

  const androidUsers = report?.platform?.android;
  const iOSUsers = report?.platform?.ios ?? 0;
  const iosSubscriptions = report?.platform?.iosSubscriptions ?? 0;
  const androidSubscriptions = report?.platform?.androidSubscriptions ?? [];
  const annuallyRegistration = report?.registration ?? [];
  const last7DaysRegistration = report?.last7Days ?? [];
  const sales = report?.sales ?? [];
  // Initial redux value is `[]` until the first successful fetch.
  const isSalesLoading = !report?.platform;

  useEffect(() => {
    GetDashboardReportCall();
    if (!hideMap) GetMapReportCall();
  }, [hideMap]);

  const content = isSalesLoading ? (
    <MDBox py={3}>
      <StatsLoadingSkeleton />
    </MDBox>
  ) : (
    <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="apple"
                title="iOS Users"
                count={iOSUsers}
                percentage={{
                  color: "success",
                  // amount: "+55%",
                  label: "Total number",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="android"
                title="Android Users"
                color="success"
                count={androidUsers}
                percentage={{
                  color: "success",
                  label: "Total number",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="apple"
                title="iOS Subscriptions"
                count={iosSubscriptions}
                percentage={{
                  color: "success",
                  label: "iOS Subscriptions",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="android"
                title="Android Subscriptions"
                count={androidSubscriptions}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Just updated",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              {!isEmpty(last7DaysRegistration) && (
                <MDBox mb={3}>
                  <ReportsBarChart
                    color="info"
                    title="Registrations"
                    description={
                      <>
                        (<strong>{annuallyRegistration?.percentage}%</strong>)
                        Compared to previous month
                      </>
                    }
                    date="just updated"
                    chart={last7DaysRegistration}
                  />
                </MDBox>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {!isEmpty(annuallyRegistration) && (
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="success"
                    title="Annually Registrations"
                    description={
                      <>
                        (<strong>{annuallyRegistration?.percentage}%</strong>)
                        Compared to previous month
                      </>
                    }
                    date="just updated"
                    chart={annuallyRegistration}
                  />
                </MDBox>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {!isEmpty(sales) && (
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="error"
                    title="Subscriptions"
                    description={
                      <>
                        (<strong>{sales?.percentage}%</strong>) Compared to
                        previous month
                      </>
                    }
                    date="just updated"
                    chart={sales}
                  />
                </MDBox>
              )}
            </Grid>
          </Grid>
        </MDBox>
        {!hideMap ? (
        <MDBox>
          <Grid>
            <MDBox
              borderRadius="12px"
              mb={3}
              sx={(theme) => ({
                width: "100%",
                padding: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
                height: "100%",
                bgcolor: darkMode ? "#0c0f14" : "background.paper",
                [theme.breakpoints.down("md")]: {
                  marginLeft: -3,
                  marginRight: -3,
                  marginBottom: 0,
                  width: "calc(100% + 48px)",
                  maxWidth: "100vw",
                  paddingLeft: 0,
                  paddingRight: 0,
                  paddingTop: 1,
                  paddingBottom: 0,
                  borderRadius: 0,
                },
              })}
            >
              <MDBox
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  padding: { xs: 0, sm: 1, md: 2 },
                  minHeight: { xs: 520, sm: 400, md: 300 },
                  height: { xs: 520, sm: 400, md: "auto" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SimpleMap />
              </MDBox>
            </MDBox>
          </Grid>
        </MDBox>
        ) : null}
      </MDBox>
  );

  if (noLayout) return content;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
